import type { Node, NodeProps } from '@xyflow/react';
import React, { useMemo } from 'react';
import { cn, makeDataCy } from '../../../helpers/util';
import { useAppSelector } from '../../../redux/hooks';
import { selectMoving } from '../../../redux/slices/diagram';
import BaseNode, { type BaseNodeData } from './base-node.component';

type EscalationFactorBarrierNodeData = BaseNodeData;
export type EscalationFactorBarrierNodeType = Node<EscalationFactorBarrierNodeData>;
type EscalationFactorBarrierNodeProps = NodeProps<EscalationFactorBarrierNodeType>;

// Control-like styling for barrier (CAA convention - neutral, same as normal controls)
const className = 'bt-bg-efb-bg bt-border-efb-border bt-text-efb-fg bt-border-dashed';
const iconClassName = 'bt-text-efb-bg bt-stroke-efb-fg';

/**
 * A component that represents an escalation factor barrier (erosion control) node in a bowtie diagram.
 * These barriers connect to escalation factors to control or mitigate them.
 * Styled to match control nodes per CAA convention.
 *
 * @component
 * @param {EscalationFactorBarrierNodeProps} props - The properties for the escalation factor barrier node component
 * @returns {JSX.Element} A rendered escalation factor barrier node component
 *
 * @example
 * ```tsx
 * <EscalationFactorBarrierNode
 *   id="escalation-barrier-1"
 *   data={{ label: "Erosion Control" }}
 *   position={{ x: 100, y: 100 }}
 * />
 * ```
 */
const EscalationFactorBarrierNode = (props: EscalationFactorBarrierNodeProps): React.JSX.Element => {
  const moving = useAppSelector(selectMoving);

  const data = useMemo(() => {
    const modifiedClassName = cn(className, { 'bt-animate-wobble': moving });
    return {
      ...props.data, // Preserve handles and other properties
      className: modifiedClassName,
      iconClassName,
      showRecordIndicators: true,
      showUnlinkActionIcon: true,
    };
  }, [props.data, moving]);

  const dataCyLabel = useMemo(() => makeDataCy(props.data.label ?? ''), [props.data.label]);

  return (
    <div data-cy={`parent-escalation-factor-barrier-node-container-${dataCyLabel}`} className="bt-flex bt-items-center">
      <BaseNode {...props} data={data} />
    </div>
  );
};

export default EscalationFactorBarrierNode;
export const id = 'escalation-factor-barrier-node';
